import { Resources } from '@shared/localizations/Resources';
import { MessageBoxButtonsTextKey, MessageBoxType } from '@shared/utils/Interfaces/Screens/MessageBox.types';
import { FocusElement } from '@shared/utils/Interfaces/Screens/OrderEntryScreen.types';
import { RuleType, RulesSet } from '@shared/utils/Rules/RulesSet';
import { ConfirmationTypesEnum } from '@shared/utils/Trading/ConfirmationTypesEnum';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { PositionActionEnum } from '@shared/utils/Trading/PositionActionEnum';
function showErrorMessage(applicationScreensProvider, message) {
    if (isValidString(message) === false) {
        return;
    }
    const messageBoxParameters = {
        type: MessageBoxType.Warning,
        title: Resources.getResource('MessageBox.ThemeChanged.Title'),
        message: message,
        okText: Resources.getResource(MessageBoxButtonsTextKey.Ok)
    };
    return applicationScreensProvider.showMessageBox(messageBoxParameters);
}
export function getActionVisibility(action, _allPositions, selectedPositions, tradingProvider) {
    const positionsProvider = tradingProvider.getPositionsProvider();
    if (isNullOrUndefined(positionsProvider)) {
        return false;
    }
    switch (action) {
        case PositionActionEnum.Mutual:
            return tradingProvider.isAllowRule(RulesSet.FUNCTION_MUTUAL_CLOSE, RuleType.User);
        case PositionActionEnum.ExerciseOption:
            return positionsProvider.isExerciseOptionVisible(selectedPositions);
        case PositionActionEnum.ExerciseCancel:
            return positionsProvider.isExerciseCancelVisible(selectedPositions);
        case PositionActionEnum.ModifyProductType:
            return tradingProvider.isAllowRule(RulesSet.FUNCTION_TRADING_MODE_MODIFICATION, RuleType.User);
        default:
            return true;
    }
}
export function getActionEnability(action, allPositions, selectedPositions, tradingProvider) {
    const allowTradingResponse = tradingProvider.isAllowTrading();
    if (allowTradingResponse.isAllowed === false) {
        return allowTradingResponse;
    }
    const positionsProvider = tradingProvider.getPositionsProvider();
    if (isNullOrUndefined(positionsProvider)) {
        return { isAllowed: false, notAllowedReason: '' };
    }
    switch (action) {
        case PositionActionEnum.All:
            return positionsProvider.isCloseAllowed(allPositions);
        case PositionActionEnum.CLXAll:
            return positionsProvider.isCLXAllowed(allPositions);
        case PositionActionEnum.CLXByAccountAndInstrument:
            return positionsProvider.isCLXByAccountAndInstrumentAllowed(selectedPositions);
        case PositionActionEnum.ByAccountAndInstrument:
            return positionsProvider.isCloseByAccountAndInstrumentAllowed(selectedPositions);
        case PositionActionEnum.ClosePositionMkt:
            return positionsProvider.isCloseMktAllowed(selectedPositions);
        case PositionActionEnum.ClosePositionViaOE:
            return positionsProvider.isCloseViaOEAllowed(selectedPositions);
        case PositionActionEnum.Positive:
            return positionsProvider.isClosePositiveAllowed(allPositions);
        case PositionActionEnum.Negative:
            return positionsProvider.isCloseNegativeAllowed(allPositions);
        case PositionActionEnum.Long:
            return positionsProvider.isCloseLongAllowed(allPositions);
        case PositionActionEnum.Short:
            return positionsProvider.isCloseShortAllowed(allPositions);
        case PositionActionEnum.ReverseByIds:
            return positionsProvider.isReverseAllowed(selectedPositions);
        case PositionActionEnum.ReverseByInstrument:
            return positionsProvider.isReverseByInstrumentAllowed(selectedPositions);
        case PositionActionEnum.ReverseAll:
            return positionsProvider.isReverseAllowed(allPositions);
        case PositionActionEnum.Mutual:
            return positionsProvider.isMutualCloseAllowed(selectedPositions);
        case PositionActionEnum.PositionModifying:
            return positionsProvider.isModifyAllowed(selectedPositions);
        case PositionActionEnum.ModifyProductType:
            return positionsProvider.isModifyProductTypeAllowed(selectedPositions);
        case PositionActionEnum.ExerciseOption:
            return positionsProvider.isExerciseOptionAllowed(selectedPositions);
        case PositionActionEnum.ExerciseCancel:
            return positionsProvider.isExerciseCancelAllowed(selectedPositions);
    }
}
export function processAction(action, allPositions, selectedPositions, appSettings, tradingProvider, applicationScreensProvider, placedFrom) {
    const positionsProvider = tradingProvider.getPositionsProvider();
    switch (action) {
        case PositionActionEnum.All:
            positionsProvider.closePositions(allPositions, placedFrom);
            break;
        case PositionActionEnum.CLXAll:
            positionsProvider.clxPositions(allPositions, placedFrom);
            break;
        case PositionActionEnum.CLXByAccountAndInstrument:
            positionsProvider.clxPositionsByAccountAndInstrument(allPositions, selectedPositions[0].getAccount(), selectedPositions[0].getInstrument(), placedFrom);
            break;
        case PositionActionEnum.ByAccountAndInstrument:
            positionsProvider.closePositionsByAccountAndInstrument(allPositions, selectedPositions[0].getAccount(), selectedPositions[0].getInstrument(), placedFrom);
            break;
        case PositionActionEnum.ClosePositionMkt:
            if (appSettings.isUseConfirmation(ConfirmationTypesEnum.PositionClose)) {
                for (const position of selectedPositions) {
                    applicationScreensProvider.showClosePositionScreen(position, placedFrom);
                }
            }
            else {
                positionsProvider.closePositions(selectedPositions, placedFrom);
            }
            break;
        case PositionActionEnum.ClosePositionViaOE:
            for (const position of selectedPositions) {
                applicationScreensProvider.showOrderEntryScreen({
                    account: position.getAccount(),
                    instrument: position.getInstrument(),
                    isLong: position.getOperationType() !== OperationType.Buy,
                    quantity: position.getAmountLots()
                });
            }
            break;
        case PositionActionEnum.Positive:
            positionsProvider.closePositionsPositive(allPositions, placedFrom);
            break;
        case PositionActionEnum.Negative:
            positionsProvider.closePositionsNegative(allPositions, placedFrom);
            break;
        case PositionActionEnum.Long:
            positionsProvider.closePositionsLong(allPositions, placedFrom);
            break;
        case PositionActionEnum.Short:
            positionsProvider.closePositionsShort(allPositions, placedFrom);
            break;
        case PositionActionEnum.ReverseByIds:
            positionsProvider.reversePositions(selectedPositions, placedFrom);
            break;
        case PositionActionEnum.ReverseByInstrument:
            positionsProvider.reversePositionsByInstrument(allPositions, selectedPositions[0].getInstrument(), placedFrom);
            break;
        case PositionActionEnum.ReverseAll:
            positionsProvider.reversePositions(allPositions, placedFrom);
            break;
        case PositionActionEnum.Mutual:
            applicationScreensProvider.showMutualClosePositionsScreen(selectedPositions, placedFrom);
            break;
        case PositionActionEnum.PositionModifying:
            applicationScreensProvider.showModifyPositionScreen(selectedPositions[0], placedFrom);
            break;
        case PositionActionEnum.ModifyProductType:
            applicationScreensProvider.showModifyPositionScreen(selectedPositions[0], placedFrom, FocusElement.ProductType);
            break;
        case PositionActionEnum.ExerciseOption:
            applicationScreensProvider.showExerciseOptionScreen(selectedPositions[0], placedFrom);
            break;
        case PositionActionEnum.ExerciseCancel:
            applicationScreensProvider.showExerciseOptionCancelScreen(selectedPositions[0], placedFrom);
            break;
    }
}
export function processTableButtonAction(action, allPositions, selectedPositions, appSettings, tradingProvider, applicationScreensProvider, placedFrom) {
    const isAllowAction = getActionEnability(action, allPositions, selectedPositions, tradingProvider);
    if (isAllowAction.isAllowed === true) {
        processAction(action, allPositions, selectedPositions, appSettings, tradingProvider, applicationScreensProvider, placedFrom);
    }
    else {
        showErrorMessage(applicationScreensProvider, isAllowAction.notAllowedReason);
    }
}
export function processTableModifySLTPAction(position, isSL, tradingProvider, applicationScreensProvider, placedFrom) {
    const positionsProvider = tradingProvider.getPositionsProvider();
    const response = isSL
        ? positionsProvider.isModifySLAllowed(position)
        : positionsProvider.isModifyTPAllowed(position);
    if (response.isAllowed === true) {
        applicationScreensProvider.showModifyPositionScreen(position, placedFrom, isSL ? FocusElement.StopLoss : FocusElement.TakeProfit);
    }
    else {
        showErrorMessage(applicationScreensProvider, response.notAllowedReason);
    }
}
